/**
 * ONE governed dashboard data executor, for every host (ROOT 68246, #1188 item 6).
 *
 * ── why it lives here ──────────────────────────────────────────────────────
 * This was `dashboardItemContent` in `apps/portal/src/data.tsx`, and the
 * Console had no equivalent at all — so a governed list or chart composed onto
 * a Console page had nothing to populate it. The obvious repair is to copy the
 * portal hook into the console, and that is the repair this file exists to
 * avoid: a second implementation of a governed read is a second READ DOOR, and
 * the two drift the first time one is edited.
 *
 * It is framework-free on purpose. No React, no query client, no host state —
 * a host wraps it in whatever adapter it already uses, and both hosts get the
 * same transport, the same refusals and the same bounds.
 *
 * ── what it does NOT do ────────────────────────────────────────────────────
 * It does not import `@solum/experience`. Experience already depends on this
 * package, and reversing that would make a cycle. So the chart GRAMMAR — the
 * `ChartDefinition` type, `validateChartDefinition` and the source-binding
 * check — stays in Experience, and a host composes the rows returned here with
 * the definition validated there. This module knows about rows and doors; it
 * has no opinion about what a chart means.
 *
 * It also makes no security decision. Every read goes through a governed door
 * as the CALLER — `core.view` and `core.dashboard_source` are read under the
 * caller's own RLS, and a source's read door is executed as an invoker RPC — so
 * "this list is not available to you" is the database's answer, not this file's.
 */
import type { SolumClient } from './client.js'
import { filtersToPostgrest, sortToPostgrest, type FilterLeaf } from './filters.js'

/** The part of `core.view` a dashboard list needs. */
export interface DashboardViewRow {
  id: string
  entity_type: string
  columns: unknown
  filters: unknown
  sort_spec: unknown
}

/** The part of `core.dashboard_source` a chart needs. */
export interface DashboardSourceRow {
  key: string
  read_door: string
  contract_version: number
}

export interface DashboardSourceColumn { key: string; label: string }

/**
 * What a host gets back. Deliberately rows-and-columns only: the chart
 * DEFINITION is Experience's, and a host composes the two.
 */
export interface DashboardRows {
  columns: DashboardSourceColumn[]
  rows: Record<string, unknown>[]
}

/** A dashboard tile is a GLANCE. An unbounded read behind one is a scan. */
export const TILE_ROW_LIMIT = 8

/** The contract version this transport understands. */
export const DASHBOARD_SOURCE_CONTRACT_VERSION = 1

/** `["name","status"]` — anything else is a view this host cannot read honestly. */
export function viewIdentifiers(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((c): c is string => typeof c === 'string' && /^[a-z_][a-z0-9_]*$/i.test(c))
    : []
}

/** `schema.fn`, or nothing callable. Never concatenated from parts. */
export function splitReadDoor(value: string): { schema: string; fn: string } {
  const match = /^([a-z_][a-z0-9_]*)\.([a-z_][a-z0-9_]*)$/u.exec(value ?? '')
  if (!match) throw new Error('This dashboard source has no callable governed read door.')
  return { schema: match[1]!, fn: match[2]! }
}

/**
 * A governed LIST: the registered view, its entity, its labels, its filters and
 * sort, bounded.
 *
 * `id` travels whether or not the view declares it, because a drill needs the
 * row identity; the caller renders only the declared columns.
 */
export async function readGovernedList(
  client: SolumClient, viewId: string,
): Promise<DashboardRows> {
  const [view] = await client.raw().select<DashboardViewRow>('core', 'view', {
    where: { id: viewId }, limit: 1,
  })
  if (!view) throw new Error('This list is not available to you.')

  const entity = await client.entity(view.entity_type)
  if (!entity) throw new Error('This list entity is not available to you.')

  // Labels through the governed door, not a raw metamodel table read.
  const semantic = await client.raw().rpcScalar<{
    fields?: { column_name: string; label: string; kind?: string }[]
  }>('describe_semantics', { p_entity_type: view.entity_type }, 'core')
  const fieldByKey = new Map((semantic?.fields ?? []).map(f => [f.column_name, f]))

  const columns = viewIdentifiers(view.columns)
  if (columns.length === 0) throw new Error('This governed view declares no readable columns.')

  const leaves = Array.isArray(view.filters) ? view.filters as FilterLeaf[] : []
  const filters = filtersToPostgrest(leaves, { kindOf: key => fieldByKey.get(key)?.kind })
  /* REFUSED, not dropped. A filter this host cannot translate faithfully would
     silently widen the list — the wrong rows, presented as the governed ones. */
  if (!filters) throw new Error('This governed view uses a filter this host cannot read faithfully.')

  const selected = columns.includes('id') ? columns : ['id', ...columns]
  const rows = await entity.list<Record<string, unknown>>({
    columns: selected.join(','),
    filters,
    order: sortToPostgrest(Array.isArray(view.sort_spec) ? view.sort_spec : []) ?? undefined,
    limit: TILE_ROW_LIMIT,
  })

  return {
    columns: columns.map(key => ({ key, label: fieldByKey.get(key)?.label ?? key })),
    rows,
  }
}

/**
 * A governed SOURCE: the registered door, its contract version, and the rows it
 * returns when executed as the caller.
 *
 * The contract version is checked here rather than at the renderer because a
 * source that has moved on is a transport question — the rows would arrive in a
 * shape this reader does not know, and guessing is how a chart mislabels one
 * source as another.
 */
export async function readGovernedSource(
  client: SolumClient, sourceKey: string,
): Promise<DashboardRows> {
  const [source] = await client.raw().select<DashboardSourceRow>('core', 'dashboard_source', {
    where: { key: sourceKey }, limit: 1,
  })
  if (!source) throw new Error('This chart source is not available to you.')
  if (source.contract_version !== DASHBOARD_SOURCE_CONTRACT_VERSION) {
    throw new Error(
      `This chart source uses unsupported contract version ${source.contract_version}.`)
  }
  const door = splitReadDoor(source.read_door)
  const rows = await client.raw().rpcTable<Record<string, unknown>>(door.fn, {}, door.schema)
  return { columns: [], rows }
}

/** What a host names when it asks for a tile's rows. */
export interface DashboardItemRef {
  kind: string
  viewId?: string | null
  sourceKey?: string | null
}

/**
 * The one entry point both hosts call.
 *
 * Returns `null` for a tile that carries no data of its own — a KPI reads its
 * measure elsewhere — so a host can tell "nothing to fetch" from "the fetch
 * refused", which are different states on the surface.
 */
export async function readDashboardItemRows(
  client: SolumClient, item: DashboardItemRef,
): Promise<DashboardRows | null> {
  if (item.kind === 'list') {
    if (!item.viewId) throw new Error('This list has no governed view.')
    return readGovernedList(client, item.viewId)
  }
  if (item.kind === 'chart') {
    if (!item.sourceKey) throw new Error('This chart has no governed source.')
    return readGovernedSource(client, item.sourceKey)
  }
  return null
}
